import { useState, useMemo, useEffect, useRef } from "react";
import { useAuth } from "../lib/AuthContext";
import { useSignInPrompt } from "./AuthGate";
import Chat from "./Chat";
import RecipeTransformer from "./RecipeTransformer";
import MealBuilder from "./MealBuilder";
import MenuDecoder from "./MenuDecoder";
import GroceryOptimizer from "./GroceryOptimizer";
import Profile from "./Profile";
import Dashboard from "./Dashboard";
import PlateView from "./PlateView";
import MealPlanner from "./MealPlanner";
import NudgeToast from "./NudgeToast";
import AdminPanel from "./AdminPanel";
import { trackSessionStart, trackTabNavigation, identifyUser } from "../lib/analytics";
import {
  HomeIcon,
  SearchIcon,
  ProfileIcon,
} from "./Icons";

type Tab = "home" | "search" | "profile" | "meal" | "recipe" | "menu" | "grocery" | "plate" | "planner" | "admin";

const PRIMARY_TABS: { id: Tab; icon: React.ComponentType<{ className?: string }>; label: string }[] = [
  { id: "home", icon: HomeIcon, label: "Home" },
  { id: "search", icon: SearchIcon, label: "Search" },
  { id: "profile", icon: ProfileIcon, label: "Profile" },
];

export default function MainApp() {
  const { user } = useAuth();
  const { promptSignIn } = useSignInPrompt();
  const [activeTab, setActiveTab] = useState<Tab>(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("admin") === "true") {
      window.history.replaceState({}, "", window.location.pathname);
      return "admin";
    }
    // Anonymous users start on Search tab
    return "search";
  });
  const mainRef = useRef<HTMLElement>(null);

  // Track session start and identify user
  useEffect(() => {
    trackSessionStart();
    if (user?.uid) {
      identifyUser(user.uid);
    }
  }, [user]);

  // Reset to Search tab on sign-out
  const resolvedTab = useMemo(() => {
    if (!user && activeTab !== "search") return "search";
    return activeTab;
  }, [user, activeTab]);

  // Reset scroll on tab change
  useEffect(() => {
    if (mainRef.current) mainRef.current.scrollTop = 0;
  }, [resolvedTab]);

  const handleTabChange = (tab: Tab) => {
    // Gate auth-required tabs for anonymous users
    if (!user && tab !== "search") {
      const reasons: Record<string, string> = {
        home: "Sign in to access your dashboard and tools",
        profile: "Sign in to manage your profile and settings",
      };
      promptSignIn(reasons[tab] || "Sign in to access this feature");
      return;
    }
    trackTabNavigation(tab, resolvedTab);
    setActiveTab(tab);
  };

  return (
    <div className="fixed inset-0 flex flex-col bg-bg">
      {/* Scrollable Content */}
      <main ref={mainRef} className="flex-1 min-h-0 overflow-y-auto">
        <div className="max-w-[440px] mx-auto px-5 py-6">
          {resolvedTab === "search" && <Chat />}
          {/* Auth-gated pages — only render if signed in */}
          {user && resolvedTab === "home" && <Dashboard onNavigate={handleTabChange} />}
          {user && resolvedTab === "plate" && <PlateView onBack={() => handleTabChange("home")} />}
          {user && resolvedTab === "planner" && <MealPlanner onBack={() => handleTabChange("home")} />}
          {user && resolvedTab === "recipe" && <RecipeTransformer onBack={() => handleTabChange("home")} />}
          {user && resolvedTab === "meal" && <MealBuilder onBack={() => handleTabChange("home")} />}
          {user && resolvedTab === "menu" && <MenuDecoder onBack={() => handleTabChange("home")} />}
          {user && resolvedTab === "grocery" && <GroceryOptimizer onBack={() => handleTabChange("home")} />}
          {user && resolvedTab === "profile" && <Profile />}
          {user && resolvedTab === "admin" && <AdminPanel />}
        </div>

        {/* Disclaimer */}
        <div className="max-w-[440px] mx-auto px-5 pb-4">
          <p className="text-[11px] text-muted text-center font-[family-name:--font-ui]">
            GI values are estimates. Consult a healthcare provider for medical advice.
          </p>
        </div>
      </main>

      {/* Bottom Navigation — all viewports */}
      <nav
        className="flex-shrink-0 border-t border-border-light bg-bg"
        style={{ paddingBottom: "max(0.5rem, env(safe-area-inset-bottom))" }}
      >
        <div className="max-w-[440px] mx-auto flex justify-around items-center pt-2">
          {PRIMARY_TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = resolvedTab === tab.id ||
              // Highlight "Home" for sub-pages
              (tab.id === "home" && ["meal", "recipe", "menu", "grocery", "plate", "planner"].includes(resolvedTab));
            const isLocked = !user && tab.id !== "search";
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`flex-1 py-2 flex flex-col items-center gap-0.5 ${
                  isActive ? "text-accent" : isLocked ? "text-muted/40" : "text-muted"
                }`}
              >
                <Icon className="w-6 h-6" />
                <span className="text-[10px] font-medium font-[family-name:--font-ui]">{tab.label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* Nudge Toast — only for signed-in users */}
      {user && <NudgeToast />}
    </div>
  );
}
